import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Components
import { IconSearch } from '@u21/tabler-icons';
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';

// Actions
import { eventsSearch } from 'app/modules/search/actions';

// Selectors
import {
  selectEventsSearchResults,
  selectEventsSearchLoading,
} from 'app/modules/search/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Utils

// Helpers
import { formatTransactionOptions } from 'app/modules/search/helpers';
import { selectSearchV2Enabled } from 'app/shared/featureFlags/selectors';
import {
  MatchType,
  TransactionEventSearchOptions,
} from 'app/modules/search/enum';
import {
  searchTransaction,
  useTransactionSearch,
} from 'app/modules/transactions/queries/useTransactionSearch';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionSearchPayload } from 'app/modules/search/payload';
import { TRANSACTION_QUERY_KEYS } from 'app/modules/transactions/queries/keys';
import { selectHasReadEventsPermission } from 'app/modules/session/selectors';

export type U21TransactionEventSearchProps<TClearable extends boolean> =
  DistributiveOmit<
    U21SelectSearchProps<string, TClearable>,
    'options' | 'onSearch'
  >;

export const U21TransactionEventSearch = <TClearable extends boolean>(
  props: U21TransactionEventSearchProps<TClearable>,
) => {
  const { value } = props;

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [newPayload, setNewPayload] = useState<TransactionSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const { data: v1Data } = useSelector(selectEventsSearchResults);
  const v1Loading = useSelector(selectEventsSearchLoading);
  const hasReadEventsPermissions = useSelector(selectHasReadEventsPermission);

  const isV2SearchUsed = useMemo(
    () => Boolean(isSearchV2Enabled),
    [isSearchV2Enabled],
  );

  const {
    refreshTransactions,
    transactions,
    query: { isFetching },
  } = useTransactionSearch(newPayload, isV2SearchUsed);

  const data = useMemo(
    () => (isV2SearchUsed ? transactions : v1Data),
    [transactions, isV2SearchUsed, v1Data],
  );

  const loading = useMemo(
    () => (isV2SearchUsed ? isFetching : v1Loading),
    [isFetching, isV2SearchUsed, v1Loading],
  );

  const options = useMemo(() => formatTransactionOptions(data), [data]);

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadEventsPermissions) {
      return;
    }

    let missingValues: string[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }

    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;
      if (isV2SearchUsed) {
        const payload: TransactionSearchPayload = {
          offset: OFFSET_DEFAULT,
          limit: missingValues.length,
          search_conditions: [
            {
              phrases: missingValues.map(String),
              field: TransactionEventSearchOptions.EXTERNAL_ID,
              match_type: MatchType.EXACT,
            },
          ],
        };

        queryClient.prefetchQuery({
          queryKey: TRANSACTION_QUERY_KEYS.getTransactionSearch(payload),
          queryFn: () => searchTransaction(payload),
        });

        setNewPayload(payload);
      } else {
        missingValues.map((i) =>
          dispatch(
            eventsSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: String(i),
              type: 'external_id',
            }),
          ),
        );
      }
    }
  }, [
    dispatch,
    hasReadEventsPermissions,
    isV2SearchUsed,
    options,
    queryClient,
    value,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        if (isV2SearchUsed) {
          setNewPayload({
            offset: OFFSET_DEFAULT,
            limit: INFINITE_SCROLL_LIMIT,
            search_conditions: [
              {
                phrases: [query],
                field: TransactionEventSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          });
        } else {
          dispatch(
            eventsSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: query || '',
              type: 'external_id',
            }),
          );
        }
      }
    },
    [dispatch, isV2SearchUsed],
  );

  if (!hasReadEventsPermissions) {
    return null;
  }

  return (
    <U21SelectSearch
      loading={loading}
      onExactSearch={
        isV2SearchUsed
          ? async (searchTerms) => {
              const payload: TransactionSearchPayload = {
                offset: OFFSET_DEFAULT,
                limit: searchTerms.length,
                search_conditions: [
                  {
                    phrases: searchTerms.map(String),
                    field: TransactionEventSearchOptions.EXTERNAL_ID,
                    match_type: MatchType.EXACT,
                  },
                ],
              };

              await queryClient.prefetchQuery({
                queryKey: TRANSACTION_QUERY_KEYS.getTransactionSearch(payload),
                queryFn: () => searchTransaction(payload),
              });
              refreshTransactions();
            }
          : undefined
      }
      onSearch={onSearch}
      options={options}
      startIcon={<IconSearch />}
      noOptionsText={
        searchValue
          ? 'No results found'
          : 'Type to search or paste a list of Transaction IDs'
      }
      {...props}
    />
  );
};
